import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// location.key → scrollY. Module-level: aynı history entry'ye (navigate(-1) / tarayıcı geri)
// dönünce aynı key ile pozisyon bulunur; yeni entry (menüden / link ile açma) için kayıt
// olmaz → en üstten başlar.
const scrollPositions = new Map();

/**
 * Site-geneli scroll yönetimi — router kökünde TEK kez mount edilir, her sayfaya uygulanır.
 * Per-sayfa scroll kodu gerektirmez.
 *
 * Davranış (react-router useNavigationType ile):
 *  - POP   (tarayıcı geri/ileri, useGoBack'in navigate(-1)'i): bu history entry için kayıtlı
 *           scroll pozisyonuna döner (async liste içeriği için kısa retry penceresiyle).
 *  - PUSH  (menüden / link ile yeni sayfaya geçiş): en üstten başlar (temiz).
 *  - REPLACE (useUrlState filtre/arama URL güncellemesi → {replace:true}): scroll'a DOKUNMAZ,
 *           yoksa her tuş vuruşunda sayfa yukarı zıplardı.
 */
export default function ScrollManager() {
  const { key } = useLocation();
  const navType = useNavigationType();
  const timerRef = useRef(null);

  // Mevcut sayfanın scroll'unu sürekli (throttled) kaydet — navigasyondan önce hazır olsun.
  useEffect(() => {
    let ticking = false;
    const save = () => { scrollPositions.set(key, window.scrollY); };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(() => { save(); ticking = false; });
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      save();
      window.removeEventListener('scroll', onScroll);
    };
  }, [key]);

  // Navigasyon tipine göre scroll'u ayarla.
  useEffect(() => {
    // Bekleyen restore timer'ı varsa temizle (ardışık hızlı navigasyon).
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }

    // REPLACE = aynı sayfada filtre/arama URL güncellemesi → scroll korunur, dokunma.
    if (navType === 'REPLACE') return;

    if (navType === 'POP') {
      const saved = scrollPositions.get(key);
      if (saved == null) return;
      // Async liste: içerik henüz yüklenmediyse sayfa kısa olur ve scrollTo hedefe ulaşmaz;
      // hedefe varana dek (veya zaman aşımına) kısa aralıklarla tekrar dene.
      let attempts = 0;
      const tryRestore = () => {
        window.scrollTo(0, saved);
        attempts += 1;
        if (attempts < 30 && window.scrollY < saved - 1) {
          timerRef.current = window.setTimeout(tryRestore, 50);
        } else {
          timerRef.current = null;
        }
      };
      tryRestore();
    } else {
      // PUSH → yeni sayfa, en üstten başla.
      window.scrollTo(0, 0);
    }

    return () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
  }, [key, navType]);

  return null;
}
